import { PREVIEW_GROUP_OPERATOR, PREVIEW_NODE_STATUS } from './ExpressionPreview.constants';

const GROUP_OPERATOR_PALETTE_KEY = {
  [PREVIEW_GROUP_OPERATOR.AND]: 'primary',
  [PREVIEW_GROUP_OPERATOR.OR]: 'warning',
};

const STATUS_PALETTE_KEY = {
  [PREVIEW_NODE_STATUS.ADDED]: 'success',
  [PREVIEW_NODE_STATUS.REMOVED]: 'error',
};

const RAIL_WIDTH = 2;
const RAIL_INDENT = 1.5;

export const expressionPreviewWrapperStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

export const expressionPreviewHeaderRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap',
};

export const expressionPreviewHeaderSpacerStyles = {
  flex: 1,
};

export const expressionPreviewExpandToggleStyles = {
  alignSelf: 'flex-start',
  px: 0,
  py: 0.25,
  minWidth: 0,
  '&:hover': {
    bgcolor: 'transparent',
    textDecoration: 'underline',
  },
};

export const groupNodeWrapperStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
};

export const groupHeaderRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'wrap',
  cursor: 'pointer',
  userSelect: 'none',
  borderRadius: 0.5,
  px: 0.5,
  mx: -0.5,
  '&:hover': {
    bgcolor: 'action.hover',
  },
  '&:focus-visible': {
    outline: ({ palette }) => `2px solid ${palette.primary.main}`,
    outlineOffset: 2,
  },
};

export const groupChevronStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 18,
  height: 18,
  color: 'text.secondary',
};

export const groupHeaderSummaryRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  flexWrap: 'wrap',
};

export const buildGroupOperatorPillStyles = operator => {
  const key = GROUP_OPERATOR_PALETTE_KEY[operator] ?? 'primary';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    px: 0.75,
    py: 0.125,
    borderRadius: 0.5,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    bgcolor: `${key}.main`,
    color: `${key}.contrastText`,
  };
};

export const buildGroupChildrenWrapperStyles = (operator, isCollapsed) => {
  if (isCollapsed) {
    return { display: 'none' };
  }

  const key = GROUP_OPERATOR_PALETTE_KEY[operator] ?? 'primary';
  const isOr = operator === PREVIEW_GROUP_OPERATOR.OR;

  return {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
    pl: RAIL_INDENT,
    ml: 1,
    borderLeft: ({ palette }) =>
      `${RAIL_WIDTH}px ${isOr ? 'dashed' : 'solid'} ${palette[key].main}`,
  };
};

export const leafRowStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  borderRadius: 0.5,
  px: 0.5,
  py: 0.25,
};

export const leafMainRowStyles = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 0.75,
};

export const buildLeafGlyphStyles = status => {
  const key = STATUS_PALETTE_KEY[status] ?? null;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 14,
    color: key === null ? 'text.disabled' : `${key}.main`,
    fontFamily: 'monospace',
    lineHeight: '1.25rem',
  };
};

export const buildLeafTextStyles = status => {
  if (status === PREVIEW_NODE_STATUS.REMOVED) {
    return {
      flex: 1,
      color: 'text.secondary',
      textDecoration: 'line-through',
    };
  }

  return {
    flex: 1,
    color: 'text.primary',
  };
};

export const leafFieldPathStyles = {
  pl: 2.5,
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  color: 'text.secondary',
  wordBreak: 'break-word',
};

export const leafActionsRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  pl: 2.5,
  flexWrap: 'wrap',
};

export const leafExplainButtonStyles = {
  alignSelf: 'flex-start',
  px: 0,
  py: 0.25,
  minWidth: 0,
  '&:hover': {
    bgcolor: 'transparent',
    textDecoration: 'underline',
  },
};

export const leafExplainPanelStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  mt: 0.25,
  ml: 2.5,
  p: 1,
  borderRadius: 1,
  bgcolor: 'action.hover',
};

export const leafExplainStatusRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

export const buildLeafStatusBadgeStyles = status => {
  const key = STATUS_PALETTE_KEY[status] ?? null;

  if (key === null) {
    return { display: 'none' };
  }

  return {
    display: 'inline-flex',
    alignItems: 'center',
    px: 0.75,
    py: 0.125,
    borderRadius: 0.5,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    bgcolor: `${key}.main`,
    color: `${key}.contrastText`,
  };
};

export const previewEmptyHelperStyles = {
  p: 1.5,
  borderRadius: 1,
  bgcolor: ({ palette }) => palette.background.secondary,
};
